import React, {useMemo} from 'react';
import {StatusBar, View, Platform} from 'react-native';
import {
  CardStyleInterpolators,
  createStackNavigator,
} from '@react-navigation/stack';
import {NavigationContainer} from '@react-navigation/native';
import {SafeAreaView, SafeAreaProvider} from 'react-native-safe-area-context';
import MainNav from './main-navigation';
import theme from '@style';
import {linking, routes} from './route';
import {getUUID, getUrlParams, navigationRef, envConfig} from '@utils';
import {inApp} from './utils/interface';
import globalStore from './services/global.state';
import {BasicObject, SafeAny} from '@types';
import {DialogLoading} from '@basicComponents/dialog';
import {useConfirm, useToast} from '@basicComponents/modal';
import {
  getBalance,
  getMessageNotReadCount,
  postReadMessage,
} from '@services/global.service';
import {debounceTime, Subject, takeUntil, throttleTime} from 'rxjs';
import {dailyRecord, setToken, setUserInfo, setVisitor} from './app.service';
import {useVersionModal} from './common-pages/hooks/versionmodal.hooks';
import {useTranslation} from 'react-i18next';
import {resolveLanguage} from './i18n';
import {resolveBootstrapChannel, webConfig} from './services/web-config.state';
import {loadWebConfig} from './services/web-config.service';
import {ParabolaProvider} from './components/basic/parabola';
import {setupAdjust} from './utils/adjust';
import {checkUpdate as checkUpdateNew} from './utils/update';

import DetailNavTitle from './components/business/detail-nav-title';
import Spin from './components/basic/spin';
import PushBox from './components/business/push-box/push-box';
import SharePanelProvider from './components/business/share-panel/share-panel-provider';
import {ImBox} from '@/common-pages/im/im-box';
import {useChannelParams} from '@/pages/home/home.hooks';
import HomeSequential from '@/pages/home/components/home-sequential';
import {VipUpgradeProvider} from './common-pages/vip/provider/vip-upgrade-provider';
import {ModalManagerProvider} from '@basicComponents/sequential-modal/ModalManagerProvider';

const myPromiseAllSettled: PromiseConstructor['allSettled'] = <
  T extends readonly unknown[] | [],
>(
  promisesArr: T,
) => {
  let wrappedPromises = promisesArr.map(promise =>
    // instead of doing value.then(), we have to use
    // Promise.resolve(value) so that we can convert
    // a non "promise" input into a "promise", and later
    // chain it with .then()
    Promise.resolve(promise)

      // when the promise is resolved, we return an object
      // with status and the value
      .then(val => ({status: 'fulfilled' as 'fulfilled', value: val}))

      // when the promise is rejected, we return an object
      // with the status and the reason
      .catch(err => ({status: 'rejected' as 'rejected', reason: err})),
  );

  // now the wrappedPromises array will contain all the settled promises
  // we will send this to Promise.all() which will resolve every time!
  return Promise.all(wrappedPromises);
  // you can also use the polyfill from part 1 of the series and it'll work the same
};

if (!Promise.allSettled) {
  Promise.allSettled = myPromiseAllSettled;
}
import {useH5MigrationGuard} from '@/common-pages/hooks/h5-migration.hooks';
import SportsIframeHost from '@/pages/gr8-sports/gr8-host';

setVisitor(getUUID());

declare var CodePush: any;
declare var AppWithCodePush: any;
if (globalStore.isAndroid) {
  CodePush = require('react-native-code-push');
}
const Stack = createStackNavigator();
const params = getUrlParams();
const getPersistedChannel = () => {
  try {
    return typeof window === 'undefined'
      ? ''
      : window.localStorage.getItem('channel') || '';
  } catch (_) {
    return '';
  }
};
const initialExplicitChannel = String(
  params.channel || getPersistedChannel(),
).trim();

// Android 宿主、URL 或已有缓存中的明确渠道始终优先于远端默认渠道。
if (initialExplicitChannel) {
  globalStore.channel = initialExplicitChannel;
}

function App(): JSX.Element {
  if (params.code) {
    localStorage.setItem('invitationCode', params.code);
  }
  const appDistory = new Subject<boolean>();
  const {i18n} = useTranslation();
  const routeNameRef = React.useRef<null | string>();
  const remoteBundleRef = React.useRef<null | SafeAny>();
  const downloadLock = React.useRef<boolean>(false);
  const [currentRouteName, setCurrentRouteName] = React.useState('');
  const [globalLoading, setGlobalLoading] = React.useState(false);
  const [bootstrapReady, setBootstrapReady] = React.useState(
    !globalStore.isWeb,
  );
  const h5MigrationMode = useH5MigrationGuard();
  const shouldShowH5MigrationGuide = h5MigrationMode === 'guide';
  const {renderModal: renderToast, show: toastShow} = useToast();
  const {renderModal: renderDownloadToast, show: downloadToastShow} = useToast({
    backDropClose: false,
    delayClose: 5000,
  });

  const initChat = () => {
    const chatModule = require('@components/chat');
    const freshchatConfig = new chatModule.FreshchatConfig(
      '2d1ef135-943f-4f4d-b0a9-bf876574cac5',
      'c12d749c-721e-442f-ba8b-5291431f0f00',
    );
    freshchatConfig.domain = 'msdk.freshchat.com';
    chatModule.Freshchat.init(freshchatConfig);
  };

  const {renderModal: renderConfirmModal, show: confirmShow} = useConfirm(
    'Update Now',
    'Later',
  );

  /** 全局订阅 */
  const globalSubscriptions = () => {
    globalStore.globalLoading.pipe(takeUntil(appDistory)).subscribe(bool => {
      setGlobalLoading(bool);
    });
    if (!globalStore.isWeb) {
      globalStore.globalTotal.pipe(takeUntil(appDistory)).subscribe(config => {
        toastShow(config);
      });
    }
    globalStore.tokenSubject.pipe(takeUntil(appDistory)).subscribe(token => {
      if (!token) {
        globalStore.notificationSubject.next({
          messageTotalCount: 0,
          sysMessageCount: 0,
          sysUserMessageCount: 0,
        });
        return;
      }
      setTimeout(() => {
        globalStore.updateAmount.next({});
      }, 100);
    });
    globalStore.refreshNotification
      .pipe(debounceTime(500), takeUntil(appDistory))
      .subscribe(() => {
        getMessageNotReadCount().then(countInfo =>
          globalStore.notificationSubject.next(countInfo),
        );
      });
    globalStore.readNotification
      .pipe(takeUntil(appDistory))
      .subscribe(({messageId, messageType}) => {
        postReadMessage(messageId, messageType).then(() => {
          globalStore.refreshNotification.next();
        });
      });
    globalStore.updateAmount
      .pipe(throttleTime(200), takeUntil(appDistory))
      .subscribe(({gameType}) => {
        globalStore.token &&
          getBalance(gameType).then(res => {
            const {totalBalance} = res;
            const amount = totalBalance;
            globalStore.setAmount(amount);
          });
      });
  };

  const checkUpdate = async () => {
    if (__DEV__ || !envConfig.codePushKey || globalStore.isWeb) {
      return;
    }
    try {
      remoteBundleRef.current = await CodePush.checkForUpdate(
        envConfig.codePushKey,
      );
      setTimeout(() => {
        startBackDownload(remoteBundleRef.current);
      }, 5000);
      // await checkRemoteBoundle(remoteBundleRef.current);
    } catch (error) {
      console.error(error);
    }
  };

  const [chckedLang, setCheckLang] = React.useState(false);
  const checkLang = async () => {
    const cachedLanguage = await globalStore.asyncGetItem('language');
    const language = resolveLanguage(cachedLanguage);

    globalStore.lang = language;
    await i18n.changeLanguage(language);
    setCheckLang(true);
  };

  const initApp = () => {
    setToken();
    setUserInfo();
    checkLang();
    globalSubscriptions();
    globalStore.asyncGetItem('last_record_time').then(res => {
      const timeCode = parseInt(res || '0', 10);
      if (
        !timeCode ||
        timeCode < new Date(new Date().toLocaleDateString()).getTime()
      ) {
        setTimeout(() => {
          dailyRecord();
          globalStore.asyncSetItem(
            'last_record_time',
            new Date().getTime() + '',
          );
        }, 600);
      }
    });
    if (globalStore.isWeb) {
      if (inApp) {
        checkUpdateNew();
      }
    }
    if (globalStore.isAndroid) {
      checkUpdate();
      initChat();
      setTimeout(() => {
        versionModal.handleUpdate();
      }, 1000);
    }
  };

  React.useEffect(() => {
    let cancelled = false;
    const initialize = async () => {
      if (globalStore.isWeb) {
        await loadWebConfig();
      }

      const c = await globalStore.asyncGetItem('channel');
      if (cancelled) {
        return;
      }

      globalStore.channel = resolveBootstrapChannel(
        initialExplicitChannel,
        c,
        webConfig.defaultChannel,
      );
      globalStore.asyncSetItem('hasChecked', globalStore.channel);
      if (!shouldShowH5MigrationGuide) {
        initApp();
      }
      setBootstrapReady(true);
    };

    initialize();
    return () => {
      cancelled = true;
      appDistory.next(true);
      appDistory.complete();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldShowH5MigrationGuide]);

  const startBackDownload = async (remoteBund: SafeAny) => {
    if (remoteBund) {
      downloadToastShow({
        type: 'weak',
        message: i18n.t('splash.tip.hasNew'),
      });
      downloadLock.current = true;
      const bundle = await remoteBund.download();
      await bundle.install(CodePush.InstallMode.ON_NEXT_RESTART);
      await CodePush.notifyAppReady();
      if (remoteBund.isMandatory) {
        confirmShow(
          i18n.t('splash.tip.alertTitle'),
          i18n.t('splash.tip.alertContent'),
          async () => {
            setTimeout(() => {
              CodePush.restartApp();
            }, 100);
          },
          () => {},
          true,
        );
      } else {
        downloadToastShow({
          type: 'weak',
          message: i18n.t('splash.tip.alertContent'),
        });
      }
    }
  };

  const versionModal = useVersionModal(
    true,
    versionInfo => {
      if (versionInfo) {
        // 如果需要更新,就不触发弹窗
        return;
      }
    },
    false,
  );
  const checkLangRef = React.useRef(false);
  React.useEffect(() => {
    checkLangRef.current = chckedLang;
    if (chckedLang && !shouldShowH5MigrationGuide) {
      checkUpdate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chckedLang, shouldShowH5MigrationGuide]);

  const betbyRender = useMemo(() => {
    return (
      <div
        style={{
          width: '100%',
          height: '100vh',
          display: 'none',
          flexDirection: 'column',
          overflow: 'hidden',
          position: 'absolute',
          top: 0,
          left: 0,
        }}
        id="betby-container">
        <DetailNavTitle
          onBack={() => globalStore.betbyBackSubject.next('back')}
          hideServer
          title="Sports"
        />
        <div id="betby" style={{flex: 1, overflow: 'auto'}}>
          <Spin loading style={theme.fill.fill} />
        </div>
      </div>
    );
  }, []);
  useChannelParams();

  return (
    <SafeAreaProvider style={[theme.position.rel]}>
      <ModalManagerProvider>
        <React.Fragment>
          {Platform.OS === 'web' ? (
            <style type="text/css">{`
        @font-face {
          font-family: 'MaterialCommunityIcons';
          src: url(${require('react-native-vector-icons/Fonts/MaterialCommunityIcons.ttf')}) format('truetype');
        }
        ::-webkit-scrollbar{
          display: none;
        }
      `}</style>
          ) : null}
        </React.Fragment>
        <StatusBar barStyle="light-content" />
        <SafeAreaView
          style={[
            globalStore.isWeb &&
              ({
                height: '100%',
                width: '100vw',
                maxWidth: '500px',
                marginHorizontal: 'auto',
              } as BasicObject),
            theme.flex.col,
            theme.overflow.hidden,
            theme.position.rel,
          ]}>
          {!bootstrapReady ? (
            <View
              style={[
                theme.fill.fill,
                theme.flex.center,
                {minHeight: globalStore.isWeb ? ('100%' as const) : undefined},
              ]}>
              <Spin loading style={theme.fill.fill} />
            </View>
          ) : shouldShowH5MigrationGuide ? null : (
            <VipUpgradeProvider>
              <View style={[theme.fill.fill]}>
                <NavigationContainer
                  onStateChange={() => {
                    const previousRouteName = routeNameRef.current;
                    const currentRoute = navigationRef!.getCurrentRoute()!.name;
                    if (previousRouteName !== '') {
                      setCurrentRouteName(currentRoute);
                    }
                    routeNameRef.current = currentRouteName;
                  }}
                  ref={navigationRef}
                  linking={linking}>
                  <Stack.Navigator
                    screenOptions={{
                      headerShown: false,
                      cardStyleInterpolator: globalStore.isWeb
                        ? undefined
                        : CardStyleInterpolators.forHorizontalIOS,
                    }}>
                    <Stack.Screen name="Index" component={MainNav} />
                    {Object.values(routes).map(route => (
                      <Stack.Screen
                        key={route.name}
                        name={route.name}
                        component={route.component}
                        options={{headerShown: route.headerShown || false}}
                      />
                    ))}
                  </Stack.Navigator>
                </NavigationContainer>
                <ImBox />
                <SportsIframeHost />
                {betbyRender}
              </View>
            </VipUpgradeProvider>
          )}
        </SafeAreaView>
        {bootstrapReady && !shouldShowH5MigrationGuide && (
          <>
            <HomeSequential />
            <DialogLoading isVisible={globalLoading} />
            {renderToast}
            {renderDownloadToast}
            {versionModal.versionModal.renderModal}
            {/*{list.length > 0 && (*/}
            {/*  <PromotionModal*/}
            {/*    popVisible={popVisible}*/}
            {/*    setPopVisible={setPopVisible}*/}
            {/*    list={list}*/}
            {/*  />*/}
            {/*)}*/}
            <SharePanelProvider />
            {renderConfirmModal}
            <ParabolaProvider />
            <PushBox />
          </>
        )}
      </ModalManagerProvider>
    </SafeAreaProvider>
  );
}

if (globalStore.isAndroid) {
  let codePushOptions = {checkFrequency: CodePush.CheckFrequency.MANUAL};
  AppWithCodePush = CodePush(codePushOptions)(App);
}
if (globalStore.isWeb) {
  setupAdjust();
}

export default globalStore.isWeb ? App : AppWithCodePush;
